import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'
import { Tooltip } from '../overlay/Tooltip'

// SidebarNav — the dark navigation rail of the AppShell (design/components/
// app-shell.html). Always dark (theme-independent), so its palette is fixed,
// not token-driven. The active item carries aria-current + weight + background
// (not colour alone). When collapsed, items keep an accessible name + a tooltip.

export interface SidebarNavItem {
  id: string
  label: string
  icon: ReactNode
  badge?: number
  futureTag?: '1.5' | '2'
  href?: string
}

export interface SidebarNavProps {
  items: SidebarNavItem[]
  activeId: string
  collapsed?: boolean
  onItemSelect?: ((id: string) => void) | undefined
  'aria-label'?: string
}

function SidebarItem({
  item,
  active,
  collapsed,
  onSelect,
}: {
  item: SidebarNavItem
  active: boolean
  collapsed: boolean
  onSelect?: ((id: string) => void) | undefined
}) {
  const cls = cn(
    'flex items-center rounded-lg text-[12.5px] transition-colors [&_svg]:size-[17px]',
    collapsed ? 'size-[38px] justify-center' : 'w-full gap-[11px] px-2.5 py-2 text-left',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue',
    active
      ? 'bg-white/[0.07] font-semibold text-white'
      : 'text-[#9aa3b2] hover:bg-white/[0.04] hover:text-[#e7eaf0]',
  )

  const body = (
    <>
      <span className="flex flex-none" aria-hidden>
        {item.icon}
      </span>
      {!collapsed && (
        <>
          <span className="flex-1 truncate whitespace-nowrap">{item.label}</span>
          {item.badge != null && (
            <span className="rounded-pill bg-brand-blue px-[7px] py-px text-[10px] font-bold tabular-nums text-white">
              {item.badge}
            </span>
          )}
          {item.futureTag && (
            <span className="rounded-pill bg-[rgba(255,104,0,0.16)] px-1.5 py-px text-[8.5px] font-bold tracking-[0.03em] text-[#ff9d5c]">
              {item.futureTag}
            </span>
          )}
        </>
      )}
    </>
  )

  const el = item.href ? (
    <a
      href={item.href}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      className={cls}
    >
      {body}
    </a>
  ) : (
    <button
      type="button"
      onClick={() => onSelect?.(item.id)}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      className={cls}
    >
      {body}
    </button>
  )

  return collapsed ? (
    <Tooltip label={item.label} side="right">
      {el}
    </Tooltip>
  ) : (
    el
  )
}

/**
 * Item list of the AppShell's dark rail. Items with `href` render as links,
 * otherwise as buttons firing `onItemSelect(id)`; the `activeId` item carries
 * aria-current. Collapsed mode keeps accessible names and adds tooltips.
 */
export function SidebarNav({
  items,
  activeId,
  collapsed = false,
  onItemSelect,
  'aria-label': ariaLabel,
}: SidebarNavProps) {
  const { t } = useTranslation('components')
  return (
    <nav
      aria-label={ariaLabel ?? t('ui.sidebar')}
      className={cn(
        'flex flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden py-2',
        collapsed ? 'items-center px-0' : 'px-3',
      )}
    >
      {items.map((item) => (
        <SidebarItem
          key={item.id}
          item={item}
          active={item.id === activeId}
          collapsed={collapsed}
          onSelect={onItemSelect}
        />
      ))}
    </nav>
  )
}
